import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Users, Shield, Wrench, MapPin, Calendar, Clock, 
  FileText, MessageSquare, ChevronRight, Search, Filter,
  AlertTriangle, CheckCircle2, Eye, UserPlus
} from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ClientPortalManagementPage() {
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', intake_mode: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [contractors, setContractors] = useState([]);

  useEffect(() => {
    loadJobs();
    loadContractors();
  }, [filter]);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.intake_mode) params.append('intake_mode', filter.intake_mode);
      
      const res = await axios.get(`${API}/api/client-portal-jobs/all?${params}`);
      setJobs(res.data);
    } catch (err) {
      toast.error('Failed to load client portal jobs');
    } finally {
      setLoading(false);
    }
  };

  const loadContractors = async () => {
    try {
      const res = await axios.get(`${API}/api/contractors`);
      setContractors(res.data);
    } catch (err) {
      console.error('Failed to load contractors');
    }
  };

  const loadJobDetail = async (job) => {
    try {
      const res = await axios.get(`${API}/api/client-portal-jobs/${job.id}/full`);
      setSelectedJob(res.data);
    } catch (err) {
      toast.error('Failed to load job details');
    }
  };

  const assignContractor = async (jobId, contractorId) => {
    try {
      // Get the linked CRM job ID
      const job = selectedJob?.linked_crm_job;
      if (!job) {
        toast.error('No linked CRM job found');
        return;
      }
      
      await axios.post(`${API}/api/jobs/${job.id}/assign-contractor-full`, null, {
        params: { contractor_id: contractorId }
      });
      toast.success('Contractor assigned and notified');
      loadJobs();
      loadJobDetail(selectedJob.client_portal_job);
    } catch (err) {
      toast.error('Failed to assign contractor');
    }
  };

  const updateStatus = async (jobId, newStatus) => {
    try {
      await axios.patch(`${API}/api/client-portal/jobs/${jobId}/status`, null, {
        params: { status: newStatus }
      });
      toast.success('Status updated');
      loadJobs();
      if (selectedJob) {
        loadJobDetail(selectedJob.client_portal_job);
      }
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return format(new Date(dateStr), 'dd MMM yyyy HH:mm');
    } catch {
      return dateStr;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Submitted': 'bg-blue-100 text-blue-800',
      'Awaiting Review': 'bg-purple-100 text-purple-800',
      'Awaiting Triage': 'bg-amber-100 text-amber-800',
      'Booked Inspection': 'bg-cyan-100 text-cyan-800',
      'In Progress': 'bg-sky-100 text-sky-800',
      'Completed': 'bg-green-100 text-green-800',
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  const filteredJobs = jobs.filter(job => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      (job.job_reference || '').toLowerCase().includes(search) ||
      (job.site_name || '').toLowerCase().includes(search) ||
      (job.client_name || '').toLowerCase().includes(search) ||
      (job.issue_type || '').toLowerCase().includes(search)
    );
  });

  const statuses = ['Submitted', 'Awaiting Review', 'Awaiting Triage', 'Booked Inspection', 'In Progress', 'Completed'];

  return (
    <div className="space-y-6" data-testid="client-portal-management">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Client Portal Submissions</h1>
          <p className="text-slate-500">Manage jobs submitted through the client portal</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="gap-1">
            <Shield className="h-3 w-3" />
            {jobs.filter(j => j.intake_mode === 'insurance_claim').length} Claims
          </Badge>
          <Badge variant="outline" className="gap-1">
            <Wrench className="h-3 w-3" />
            {jobs.filter(j => j.intake_mode === 'maintenance_issue').length} Maintenance
          </Badge>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search jobs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 rounded-xl"
          />
        </div>
        <select
          value={filter.intake_mode}
          onChange={(e) => setFilter({ ...filter, intake_mode: e.target.value })}
          className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
        >
          <option value="">All Types</option>
          <option value="insurance_claim">Insurance Claims</option>
          <option value="maintenance_issue">Maintenance Issues</option>
        </select>
        <select
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
        >
          <option value="">All Statuses</option>
          {statuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Main Content */}
      <div className="grid gap-6 xl:grid-cols-[1fr_400px]">
        {/* Jobs List */}
        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F7A78] mx-auto"></div>
            </div>
          ) : filteredJobs.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <Users className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No client portal jobs found</p>
              </CardContent>
            </Card>
          ) : (
            filteredJobs.map(job => (
              <Card 
                key={job.id}
                className={`cursor-pointer transition-all hover:shadow-md ${selectedJob?.client_portal_job?.id === job.id ? 'ring-2 ring-[#1F7A78]' : ''}`}
                onClick={() => loadJobDetail(job)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-[#1F7A78]">{job.job_reference}</span>
                        <Badge className={getStatusColor(job.status)}>{job.status}</Badge>
                      </div>
                      <div className="flex gap-2 mb-2">
                        <Badge className={job.intake_mode === 'insurance_claim' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'} variant="outline">
                          {job.intake_mode === 'insurance_claim' ? 'Claim' : 'Maintenance'}
                        </Badge>
                        <Badge variant="outline">{job.issue_type}</Badge>
                      </div>
                      <p className="text-sm text-slate-600 flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {job.site_name}{job.plot_number && `, Plot ${job.plot_number}`}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        {job.client_name} • {formatDate(job.created_at)}
                      </p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-400" />
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Detail Panel */}
        <div className="xl:sticky xl:top-24 xl:self-start">
          {selectedJob ? (
            <div className="space-y-4">
              {/* Job Header */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-xl font-bold text-[#1F7A78]">{selectedJob.client_portal_job.job_reference}</h2>
                      <Badge className={getStatusColor(selectedJob.client_portal_job.status)}>
                        {selectedJob.client_portal_job.status}
                      </Badge>
                    </div>
                    <Badge className={selectedJob.client_portal_job.intake_mode === 'insurance_claim' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'}>
                      {selectedJob.client_portal_job.intake_mode === 'insurance_claim' ? 'Insurance Claim' : 'Maintenance'}
                    </Badge>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Issue Type:</span>
                      <span className="font-medium">{selectedJob.client_portal_job.issue_type}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Site:</span>
                      <span className="font-medium">{selectedJob.client_portal_job.site_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Client:</span>
                      <span className="font-medium">{selectedJob.client_portal_job.client_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Email:</span>
                      <span className="font-medium">{selectedJob.client_portal_job.client_email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Phone:</span>
                      <span className="font-medium">{selectedJob.client_portal_job.client_phone}</span>
                    </div>
                  </div>

                  {/* Triage Flags */}
                  {selectedJob.linked_crm_job && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-xs font-medium text-slate-500 mb-2">Auto-Triage Flags</p>
                      <div className="flex flex-wrap gap-1">
                        {selectedJob.linked_crm_job.urgent_health_and_safety && (
                          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Urgent H&S</Badge>
                        )}
                        {selectedJob.linked_crm_job.possible_insurable_event && (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Insurable Event</Badge>
                        )}
                        {selectedJob.linked_crm_job.possible_wear_and_tear && (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Wear & Tear</Badge>
                        )}
                        {selectedJob.linked_crm_job.drainage_or_ground_issue && (
                          <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">Ground Issue</Badge>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Description */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Description</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">
                    {selectedJob.client_portal_job.issue_description}
                  </p>
                </CardContent>
              </Card>

              {/* Status Update */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Update Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {statuses.map(s => (
                      <Button
                        key={s}
                        size="sm"
                        variant={selectedJob.client_portal_job.status === s ? 'default' : 'outline'}
                        onClick={() => updateStatus(selectedJob.client_portal_job.id, s)}
                        className={selectedJob.client_portal_job.status === s ? 'bg-[#1F7A78]' : ''}
                      >
                        {s}
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Assign Contractor */}
              {selectedJob.linked_crm_job && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <UserPlus className="h-4 w-4" />
                      Assign Contractor
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {selectedJob.linked_crm_job.assigned_contractor_name ? (
                      <div className="flex items-center justify-between">
                        <span className="text-sm">
                          <span className="text-slate-500">Assigned to:</span>{' '}
                          <span className="font-medium">{selectedJob.linked_crm_job.assigned_contractor_name}</span>
                        </span>
                        <Badge>{selectedJob.linked_crm_job.contractor_status}</Badge>
                      </div>
                    ) : (
                      <select
                        className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                        onChange={(e) => e.target.value && assignContractor(selectedJob.client_portal_job.id, e.target.value)}
                        defaultValue=""
                      >
                        <option value="">Select contractor...</option>
                        {contractors.map(c => (
                          <option key={c.id} value={c.id}>
                            {c.company_name || c.name} ({c.trade_type || 'General'})
                          </option>
                        ))}
                      </select>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Files */}
              {selectedJob.files?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Uploaded Files ({selectedJob.files.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-40 overflow-auto">
                      {selectedJob.files.map(f => (
                        <a
                          key={f.id}
                          href={`${API}${f.file_url}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center justify-between p-2 rounded-lg bg-slate-50 hover:bg-slate-100 text-sm"
                        >
                          <span className="truncate">{f.file_name}</span>
                          <Eye className="h-4 w-4 text-slate-400" />
                        </a>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Notes */}
              {selectedJob.notes?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      Messages ({selectedJob.notes.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-48 overflow-auto">
                      {selectedJob.notes.map(n => (
                        <div key={n.id} className="p-2 rounded-lg bg-slate-50 text-sm">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium">{n.created_by_name}</span>
                            <span className="text-xs text-slate-500">{formatDate(n.created_at)}</span>
                          </div>
                          <p className="text-slate-700">{n.message}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <Card className="border-dashed">
              <CardContent className="py-16 text-center">
                <Users className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Select a job to view details</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
